import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Setup() {
  const [value, setValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form States
  const [newUser, setNewUser] = useState({ username: '', password: '', full_name: '', role_id: '', country_id: '', division_id: '' });

  const [newRole, setNewRole] = useState('');
  const [newCountry, setNewCountry] = useState({ name: '', code: '' });
  const [newDivision, setNewDivision] = useState({ name: '', code: '' });
  const [newTicketType, setNewTicketType] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    const fetchEndpoint = async (url, setter) => {
        try {
            const res = await axios.get(url, { headers });
            setter(res.data);
        } catch (err) {
            console.error(`Error fetching ${url}:`, err);
            if (err.response && err.response.status === 401) {
                // Token might be expired
                alert("Session expired. Please logout and login again.");
            }
        }
    };

    await Promise.all([
        fetchEndpoint(`${API_URL}/setup/users`, setUsers),
        fetchEndpoint(`${API_URL}/setup/roles`, setRoles),
        fetchEndpoint(`${API_URL}/setup/countries`, setCountries),
        fetchEndpoint(`${API_URL}/setup/divisions`, setDivisions),
        fetchEndpoint(`${API_URL}/ticket-types`, setTicketTypes)
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API_URL}/setup/users`, newUser, { headers });
      fetchData();
      setNewUser({ username: '', password: '', full_name: '', role_id: '', country_id: '', division_id: '' });
    } catch (err) {
      alert('Error creating user');
    }
  };

  const handleCreateRole = async () => {
    try {
      await axios.post(`${API_URL}/setup/roles`, { name: newRole }, { headers });
      fetchData();
      setNewRole('');
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating role');
    }
  };

  const handleCreateCountry = async () => {
    try {
      await axios.post(`${API_URL}/setup/countries`, newCountry, { headers });
      fetchData();
      setNewCountry({ name: '', code: '' });
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating country');
    }
  };

  const handleCreateDivision = async () => {
    try {
      await axios.post(`${API_URL}/setup/divisions`, newDivision, { headers });
      fetchData();
      setNewDivision({ name: '', code: '' });
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating division');
    }
  };

  const handleCreateTicketType = async () => {
    try {
      await axios.post(`${API_URL}/ticket-types`, { name: newTicketType }, { headers });
      fetchData();
      setNewTicketType('');
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating ticket type');
    }
  };

  const handleDeleteTicketType = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/ticket-types/${id}`, { headers });
      fetchData();
    } catch (err) {
      alert('Error deleting ticket type');
    }
  };

  if (loading) return <Loading message="Loading Setup Data..." />;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>Setup</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={(e, v) => setValue(v)}>
          <Tab label="Users" />
          <Tab label="Roles" />
          <Tab label="Countries" />
          <Tab label="Divisions" />
          <Tab label="Ticket Types" />
        </Tabs>
      </Box>
      
      <TabPanel value={value} index={0}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField label="Username" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} />
          <TextField label="Password" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} />
          <TextField label="Full Name" value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})} />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select value={newUser.role_id} label="Role" onChange={e => setNewUser({...newUser, role_id: e.target.value})}>
              {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Country</InputLabel>
            <Select value={newUser.country_id} label="Country" onChange={e => setNewUser({...newUser, country_id: e.target.value})}>
              <MenuItem value=""><em>None</em></MenuItem>
              {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Division</InputLabel>
            <Select value={newUser.division_id} label="Division" onChange={e => setNewUser({...newUser, division_id: e.target.value})}>
              <MenuItem value=""><em>None</em></MenuItem>
              {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleCreateUser}>Add User</Button>
        </Box>
        <Table>
          <TableHead><TableRow><TableCell>Username</TableCell><TableCell>Name</TableCell><TableCell>Role</TableCell><TableCell>Country</TableCell><TableCell>Division</TableCell><TableCell>Fingerprint</TableCell></TableRow></TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.full_name}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>{u.country}</TableCell>
                <TableCell>{u.division}</TableCell>
                <TableCell>
                  {u.has_fingerprint ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                      <FingerprintIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Registered
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">Not Registered</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabPanel>
      
      <TabPanel value={value} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField label="Role Name" value={newRole} onChange={e => setNewRole(e.target.value)} />
            <Button variant="contained" onClick={handleCreateRole}>Add Role</Button>
        </Box>
        <Table>
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell></TableRow></TableHead>
            <TableBody>{roles.map(r => <TableRow key={r.id}><TableCell>{r.id}</TableCell><TableCell>{r.name}</TableCell></TableRow>)}</TableBody>
        </Table>
      </TabPanel>
      
      <TabPanel value={value} index={2}>
         <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField label="Country Name" value={newCountry.name} onChange={e => setNewCountry({...newCountry, name: e.target.value})} />
            <TextField label="Code (1 Letter)" value={newCountry.code} onChange={e => setNewCountry({...newCountry, code: e.target.value})} inputProps={{ maxLength: 1 }} />
            <Button variant="contained" onClick={handleCreateCountry}>Add Country</Button>
        </Box>
        <Table>
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
            <TableBody>{countries.map(c => <TableRow key={c.id}><TableCell>{c.id}</TableCell><TableCell>{c.name}</TableCell><TableCell>{c.code}</TableCell></TableRow>)}</TableBody>
        </Table>
      </TabPanel>
      
      <TabPanel value={value} index={3}>
         <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField label="Division Name" value={newDivision.name} onChange={e => setNewDivision({...newDivision, name: e.target.value})} />
            <TextField label="Code (1 Letter)" value={newDivision.code} onChange={e => setNewDivision({...newDivision, code: e.target.value})} inputProps={{ maxLength: 1 }} />
            <Button variant="contained" onClick={handleCreateDivision}>Add Division</Button>
        </Box>
        <Table>
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
            <TableBody>{divisions.map(d => <TableRow key={d.id}><TableCell>{d.id}</TableCell><TableCell>{d.name}</TableCell><TableCell>{d.code}</TableCell></TableRow>)}</TableBody>
        </Table>
      </TabPanel>

      <TabPanel value={value} index={4}>
         <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField label="Ticket Type Name" value={newTicketType} onChange={e => setNewTicketType(e.target.value)} />
            <Button variant="contained" onClick={handleCreateTicketType}>Add Type</Button>
        </Box>
        <Table>
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
            <TableBody>{ticketTypes.map(t => (
                <TableRow key={t.id}>
                    <TableCell>{t.id}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>
                        <Button color="error" onClick={() => handleDeleteTicketType(t.id)}>Delete</Button>
                    </TableCell>
                </TableRow>
            ))}</TableBody>
        </Table>
      </TabPanel>
    </Box>
  );
}

export default Setup;
